// PlanActionDetailPage — Gestion detaillee d'un plan d'action depuis le dashboard
// Route : /dashboard/plan-action/:id
// Utilise DashboardLayout (pas de tunnel diagnostic)

import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
// Layout is handled by route-level DashboardLayout
import { Card, CardContent, Button, Input } from '../components/ui'
import { Badge } from '../components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table'
import { Checkbox } from '../components/ui/checkbox'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip'
import { Skeleton } from '../components/ui/skeleton'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog'
import { api, type SaveActionPlanInput } from '../services/api'
import { CasqueIcons } from '../components/results/CasqueDisplay'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import {
  ClipboardList, CheckCircle2, Clock, AlertTriangle,
  ListTodo, Trash2, Save, Loader2, ChevronDown,
  ArrowLeft, ExternalLink,
} from 'lucide-react'
import { cn } from '../lib/utils'

// -- Interfaces --

interface ActionPlan {
  id: string
  solutionId: string
  theme: string
  libelle: string
  metierIds: string[]
  metierNames: Record<string, string>
  responsable: string
  dateDebut: string
  dateFin: string
  termineLe?: string
}

interface ApiActionRow {
  id: string
  solutionId: string
  metierId: string
  theme: string
  libelle: string
  responsable: string | null
  dateDebut: string | null
  dateFin: string | null
  dateRealisation: string | null
  statut: string
  metier: { code: string; nom: string }
}

interface DiagnosticInfo {
  id: string
  createdAt: string
  entreprise?: { raisonSociale: string }
  resultat?: { casques: number; scoreGlobal: number; maxGlobal?: number; maxScore?: number }
}

// -- Mapping functions --

function fromApiRows(rows: ApiActionRow[]): ActionPlan[] {
  const groups = new Map<string, { firstRow: ApiActionRow; metierCodes: string[]; metierNames: Record<string, string> }>()

  for (const row of rows) {
    const key = row.solutionId
    if (!groups.has(key)) {
      groups.set(key, { firstRow: row, metierCodes: [], metierNames: {} })
    }
    const group = groups.get(key)!
    if (!group.metierCodes.includes(row.metier.code)) {
      group.metierCodes.push(row.metier.code)
      group.metierNames[row.metier.code] = row.metier.nom
    }
  }

  return Array.from(groups.values()).map(({ firstRow, metierCodes, metierNames }) => ({
    id: firstRow.id,
    solutionId: firstRow.solutionId,
    theme: firstRow.theme,
    libelle: firstRow.libelle,
    metierIds: metierCodes,
    metierNames,
    responsable: firstRow.responsable || '',
    dateDebut: firstRow.dateDebut ? firstRow.dateDebut.split('T')[0] : '',
    dateFin: firstRow.dateFin ? firstRow.dateFin.split('T')[0] : '',
    termineLe: firstRow.dateRealisation ? firstRow.dateRealisation.split('T')[0] : undefined,
  }))
}

function toApiPayload(actions: ActionPlan[]): SaveActionPlanInput[] {
  return actions.map(action => ({
    solutionId: action.solutionId,
    metierCodes: action.metierIds,
    theme: action.theme,
    libelle: action.libelle,
    responsable: action.responsable || null,
    dateDebut: action.dateDebut || null,
    dateFin: action.dateFin || null,
    dateRealisation: action.termineLe || null,
    statut: action.termineLe
      ? 'termine' as const
      : (action.responsable && action.dateDebut) ? 'en_cours' as const : 'a_faire' as const,
  }))
}

// -- Status config --

type ActionStatus = 'non_commence' | 'en_cours' | 'echu' | 'termine'

const STATUS_CONFIG: Record<ActionStatus, {
  label: string
  icon: typeof CheckCircle2
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
  tooltip: string
}> = {
  non_commence: {
    label: 'À faire',
    icon: ListTodo,
    variant: 'secondary',
    tooltip: 'Renseignez un responsable et une date de début pour passer en "En cours"',
  },
  en_cours: {
    label: 'En cours',
    icon: Clock,
    variant: 'default',
    tooltip: "L'action a un responsable et une date de début",
  },
  echu: {
    label: 'Échu',
    icon: AlertTriangle,
    variant: 'destructive',
    tooltip: "La date d'échéance est dépassée",
  },
  termine: {
    label: 'Terminé',
    icon: CheckCircle2,
    variant: 'outline',
    tooltip: 'Action terminée',
  },
}

function getActionStatus(action: ActionPlan): ActionStatus {
  if (action.termineLe) return 'termine'
  if (action.responsable && action.dateDebut) {
    if (action.dateFin && new Date(action.dateFin) < new Date()) return 'echu'
    return 'en_cours'
  }
  return 'non_commence'
}

function capitalizeTheme(theme: string): string {
  if (!theme) return theme
  return theme.charAt(0).toUpperCase() + theme.slice(1)
}

// -- Loading skeleton --

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-4 pb-3">
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <div className="p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </Card>
    </div>
  )
}

// -- Component --

export default function PlanActionDetailPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [diagnosticInfo, setDiagnosticInfo] = useState<DiagnosticInfo | null>(null)
  const [actions, setActions] = useState<ActionPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set())
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [solutionLinks, setSolutionLinks] = useState<Map<string, string>>(new Map())
  const [showStickySave, setShowStickySave] = useState(false)
  const saveButtonRef = useRef<HTMLButtonElement>(null)

  // Show sticky save button when main button is out of view
  useEffect(() => {
    const el = saveButtonRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickySave(!entry.isIntersecting),
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isLoading])

  // Unsaved changes guard
  const { blocker, allowNavigation } = useUnsavedChangesGuard(hasUnsavedChanges)

  // Load from DB + solutions referentiel for resource links
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return
      setIsLoading(true)
      try {
        const [diagnostic, rows] = await Promise.all([
          api.getDiagnostic(diagnosticId),
          api.getActionPlans(diagnosticId),
        ])
        setDiagnosticInfo({
          id: diagnostic.id,
          createdAt: diagnostic.createdAt,
          entreprise: diagnostic.entreprise,
          resultat: diagnostic.resultat,
        })
        setActions(fromApiRows(rows))

        // Load solutions referentiel for resource links
        try {
          const res = await fetch('/data/referentiel/solutions.json')
          if (res.ok) {
            const json = await res.json()
            const links = new Map<string, string>()
            for (const sol of json.data || []) {
              if (sol.id && sol.lien) links.set(sol.id, sol.lien)
            }
            setSolutionLinks(links)
          }
        } catch { /* ignore — links are optional */ }
      } catch (err) {
        console.error('Erreur chargement plan:', err)
        toast.error('Erreur lors du chargement du plan d\'action')
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [diagnosticId])

  // Save
  const savePlan = useCallback(async (): Promise<boolean> => {
    if (!diagnosticId || actions.length === 0) return true
    setIsSaving(true)
    try {
      await api.saveActionPlans(diagnosticId, { actions: toApiPayload(actions) })
      setHasUnsavedChanges(false)
      toast.success('Plan d\'action sauvegardé')
      return true
    } catch (error) {
      console.error('Erreur sauvegarde plan:', error)
      toast.error('Erreur lors de la sauvegarde')
      return false
    } finally {
      setIsSaving(false)
    }
  }, [diagnosticId, actions])

  // Mutations
  const updateAction = (actionId: string, field: keyof ActionPlan, value: string | string[]) => {
    setActions(prev => prev.map(a => a.id === actionId ? { ...a, [field]: value } : a))
    setHasUnsavedChanges(true)
  }

  const removeAction = (actionId: string) => {
    setActions(prev => prev.filter(a => a.id !== actionId))
    setHasUnsavedChanges(true)
    toast('Action supprimée du plan')
  }

  const toggleComplete = (actionId: string) => {
    setActions(prev => prev.map(action => {
      if (action.id !== actionId) return action
      return {
        ...action,
        termineLe: action.termineLe ? undefined : new Date().toISOString().split('T')[0],
      }
    }))
    setHasUnsavedChanges(true)
  }

  const toggleExpand = (actionId: string) => {
    setExpandedActions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(actionId)) newSet.delete(actionId)
      else newSet.add(actionId)
      return newSet
    })
  }

  // Stats
  const stats = useMemo(() => {
    const total = actions.length
    const nonCommence = actions.filter(a => getActionStatus(a) === 'non_commence').length
    const enCours = actions.filter(a => getActionStatus(a) === 'en_cours').length
    const echu = actions.filter(a => getActionStatus(a) === 'echu').length
    const termine = actions.filter(a => getActionStatus(a) === 'termine').length
    return { total, nonCommence, enCours, echu, termine }
  }, [actions])

  // Filtered actions by tab
  const filteredActions = useMemo(() => {
    if (activeTab === 'all') return actions
    return actions.filter(a => getActionStatus(a) === activeTab)
  }, [actions, activeTab])

  // Group by theme
  const actionsByTheme = useMemo(() => {
    const grouped: Record<string, ActionPlan[]> = {}
    filteredActions.forEach(action => {
      if (!grouped[action.theme]) grouped[action.theme] = []
      grouped[action.theme].push(action)
    })
    return grouped
  }, [filteredActions])

  // Navigation
  const handleBack = async () => {
    if (hasUnsavedChanges) await savePlan()
    allowNavigation()
    navigate('/dashboard/plan-action')
  }

  return (
    <>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="mb-3 -ml-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4 mr-1" />
              Plan d'action
            </Button>

            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <ClipboardList className="size-5 text-primary" />
                </div>
                <div className="space-y-0.5">
                  <h1 className="text-2xl font-bold text-foreground tracking-tight">
                    {diagnosticInfo?.entreprise?.raisonSociale || 'Plan d\'action'}
                  </h1>
                  <div className="flex items-center gap-3">
                    {diagnosticInfo && (
                      <span className="text-sm text-muted-foreground">
                        Diagnostic du {new Date(diagnosticInfo.createdAt).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'long', year: 'numeric',
                        })}
                      </span>
                    )}
                    {diagnosticInfo?.resultat && (
                      <CasqueIcons count={diagnosticInfo.resultat.casques} size="sm" />
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {actions.length > 0 && (
                  <Button
                    ref={saveButtonRef}
                    onClick={async () => {
                      const ok = await savePlan()
                      if (ok) navigate('/dashboard/plan-action')
                    }}
                    disabled={isSaving}
                    size="lg"
                    className="shadow-sm"
                  >
                    {isSaving ? (
                      <><Loader2 className="size-4 mr-1.5 animate-spin" /> Sauvegarde...</>
                    ) : (
                      <><Save className="size-4 mr-1.5" /> Sauvegarder</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {actions.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <ClipboardList className="size-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Aucune action</h3>
                <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                  Ce diagnostic n'a pas encore de plan d'action. Terminez le diagnostic et s&eacute;lectionnez des solutions.
                </p>
                <Button variant="outline" onClick={() => navigate('/dashboard/plan-action')}>
                  Retour au plan d'action
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* KPI cards */}
              <div className="mb-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {([
                    { key: 'non_commence' as const, count: stats.nonCommence, label: 'À faire', icon: ListTodo, iconBg: 'bg-muted', iconColor: 'text-muted-foreground' },
                    { key: 'en_cours' as const, count: stats.enCours, label: 'En cours', icon: Clock, iconBg: 'bg-blue-50', iconColor: 'text-blue-600' },
                    { key: 'termine' as const, count: stats.termine, label: 'Terminées', icon: CheckCircle2, iconBg: 'bg-green-50', iconColor: 'text-green-600' },
                    { key: 'echu' as const, count: stats.echu, label: 'Échues', icon: AlertTriangle, iconBg: 'bg-red-50', iconColor: 'text-red-600' },
                  ]).map(kpi => {
                    const KpiIcon = kpi.icon
                    return (
                      <button
                        key={kpi.key}
                        onClick={() => setActiveTab(activeTab === kpi.key ? 'all' : kpi.key)}
                        className={cn(
                          'text-left transition-all rounded-xl border bg-card',
                          activeTab === kpi.key
                            ? 'ring-2 ring-primary border-primary shadow-sm'
                            : 'border-border hover:border-primary/30 hover:shadow-sm'
                        )}
                      >
                        <div className="p-3">
                          <div className="flex items-center gap-3">
                            <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', kpi.iconBg)}>
                              <KpiIcon className={cn('size-5', kpi.iconColor)} />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-foreground tabular-nums leading-none">{kpi.count}</p>
                              <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Info conseil */}

              {stats.nonCommence > 0 && (
                <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3 mb-5">
                  <Clock className="size-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-blue-800">
                    Renseignez un <strong>responsable</strong> et une <strong>date de début</strong> pour passer les actions en statut « En cours ».
                  </p>
                </div>
              )}

              {/* Tabs filter */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
                <TabsList>
                  <TabsTrigger value="all">
                    Tout <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.total}</span>
                  </TabsTrigger>
                  <TabsTrigger value="non_commence">
                    À faire <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.nonCommence}</span>
                  </TabsTrigger>
                  <TabsTrigger value="en_cours">
                    En cours <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.enCours}</span>
                  </TabsTrigger>
                  <TabsTrigger value="termine">
                    Terminé <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.termine}</span>
                  </TabsTrigger>
                  {stats.echu > 0 && (
                    <TabsTrigger value="echu">
                      Échu <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-destructive/15 text-destructive text-[11px] font-semibold tabular-nums min-w-5 h-5 px-1.5">{stats.echu}</span>
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* Content area — shared across all tabs since we filter state */}
                {['all', 'non_commence', 'en_cours', 'termine', 'echu'].map(tabKey => (
                  <TabsContent key={tabKey} value={tabKey} className="mt-4">
                    {filteredActions.length === 0 ? (
                      <Card>
                        <CardContent className="py-10 text-center">
                          <p className="text-muted-foreground">Aucune action dans cette cat&eacute;gorie.</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <>
                        {/* Desktop: table grouped by theme */}
                        <div className="hidden lg:block space-y-4">
                          {Object.entries(actionsByTheme).map(([theme, themeActions]) => {
                            const themeTermine = themeActions.filter(a => !!a.termineLe).length

                            return (
                            <Card key={theme} className="overflow-hidden pt-0">
                              <div className="flex items-center justify-between px-6 py-3 bg-muted/50 border-b">
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-bold text-primary">
                                    {capitalizeTheme(theme)}
                                  </span>
                                  <span className="text-muted-foreground text-sm">
                                    {themeActions.length} action{themeActions.length > 1 ? 's' : ''}
                                  </span>
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {themeTermine}/{themeActions.length} terminée{themeTermine > 1 ? 's' : ''}
                                </span>
                              </div>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[40px] pl-6" />
                                    <TableHead>Action</TableHead>
                                    <TableHead className="w-[120px]">M&eacute;tier</TableHead>
                                    <TableHead className="w-[150px]">Responsable</TableHead>
                                    <TableHead className="w-[140px]">Date d&eacute;but</TableHead>
                                    <TableHead className="w-[140px]">&Eacute;ch&eacute;ance</TableHead>
                                    <TableHead className="w-[110px]">Statut</TableHead>
                                    <TableHead className="w-[50px]" />
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {themeActions.map(action => (
                                    <ActionTableRow
                                      key={action.id}
                                      action={action}
                                      lien={solutionLinks.get(action.solutionId)}
                                      onUpdate={updateAction}
                                      onToggleComplete={toggleComplete}
                                      onRemove={removeAction}
                                    />
                                  ))}
                                </TableBody>
                              </Table>
                            </Card>
                            )
                          })}
                        </div>

                        {/* Mobile cards */}
                        <div className="lg:hidden space-y-3">
                          {filteredActions.map(action => (
                            <ActionMobileCard
                              key={action.id}
                              action={action}
                              lien={solutionLinks.get(action.solutionId)}
                              isExpanded={expandedActions.has(action.id)}
                              onToggleExpand={() => toggleExpand(action.id)}
                              onUpdate={updateAction}
                              onToggleComplete={toggleComplete}
                              onRemove={removeAction}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </TabsContent>
                ))}
              </Tabs>

            </>
          )}
        </>
      )}

      {/* Bouton Sauvegarder sticky visible au scroll */}
      {showStickySave && actions.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={async () => {
              const ok = await savePlan()
              if (ok) navigate('/dashboard/plan-action')
            }}
            disabled={isSaving}
            size="lg"
            className="shadow-lg"
          >
            {isSaving ? (
              <><Loader2 className="size-4 mr-1.5 animate-spin" /> Sauvegarde...</>
            ) : (
              <><Save className="size-4 mr-1.5" /> Sauvegarder</>
            )}
          </Button>
        </div>
      )}

      <UnsavedChangesDialog blocker={blocker} onSave={savePlan} />
    </>
  )
}

// -- ActionTableRow sub-component --

function ActionTableRow({ action, lien, onUpdate, onToggleComplete, onRemove }: {
  action: ActionPlan
  lien?: string
  onUpdate: (id: string, field: keyof ActionPlan, value: string | string[]) => void
  onToggleComplete: (id: string) => void
  onRemove: (id: string) => void
}) {
  const status = getActionStatus(action)
  const config = STATUS_CONFIG[status]
  const StatusIcon = config.icon
  const isComplete = !!action.termineLe

  return (
    <TableRow className={cn(
      'group transition-colors',
      isComplete && 'bg-green-50/40',
      status === 'echu' && 'bg-red-50/30',
    )}>
      {/* Checkbox */}
      <TableCell className="pl-6">
        <Checkbox
          checked={isComplete}
          onCheckedChange={() => onToggleComplete(action.id)}
          className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
        />
      </TableCell>

      {/* Action name */}
      <TableCell>
        <span className={cn(
          'text-sm font-medium transition-colors',
          isComplete && 'line-through text-muted-foreground'
        )}>
          {action.libelle}
        </span>
        {lien && (
          <a
            href={lien}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
            onClick={e => e.stopPropagation()}
          >
            <ExternalLink className="size-3" />
            Ressource OPPBTP
          </a>
        )}
      </TableCell>

      {/* Metier badges */}
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {action.metierIds.map(mid => (
            <Badge key={mid} variant="secondary" className="text-[10px] px-1.5 py-0">
              {action.metierNames[mid] || mid}
            </Badge>
          ))}
        </div>
      </TableCell>

      {/* Responsable */}
      <TableCell>
        <Input
          type="text"
          value={action.responsable}
          onChange={e => onUpdate(action.id, 'responsable', e.target.value)}
          placeholder="Nom..."
          className="h-8 text-sm"
          disabled={isComplete}
        />
      </TableCell>

      {/* Date debut */}
      <TableCell>
        <Input
          type="date"
          value={action.dateDebut}
          onChange={e => onUpdate(action.id, 'dateDebut', e.target.value)}
          className="h-8 text-sm"
          disabled={isComplete}
        />
      </TableCell>

      {/* Echeance */}
      <TableCell>
        <Input
          type="date"
          value={action.dateFin}
          onChange={e => onUpdate(action.id, 'dateFin', e.target.value)}
          className={cn(
            'h-8 text-sm',
            status === 'echu' && 'border-destructive text-destructive'
          )}
          disabled={isComplete}
        />
      </TableCell>

      {/* Status badge */}
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onToggleComplete(action.id)}
              className="inline-flex"
            >
              <Badge
                variant={config.variant}
                className={cn(
                  'cursor-pointer gap-1 transition-all hover:opacity-80',
                  isComplete && 'bg-green-100 text-green-800 border-green-200'
                )}
              >
                <StatusIcon className="size-3" />
                {config.label}
              </Badge>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{config.tooltip}</p>
            <p className="text-[10px] opacity-70 mt-0.5">
              {isComplete ? 'Cliquez pour rouvrir' : 'Cliquez pour marquer terminé'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TableCell>

      {/* Delete */}
      <TableCell>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors opacity-0 group-hover:opacity-100"
              title="Supprimer"
            >
              <Trash2 className="size-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent size="sm">
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette action ?</AlertDialogTitle>
              <AlertDialogDescription>
                L'action &laquo;&nbsp;{action.libelle}&nbsp;&raquo; sera retir&eacute;e du plan. Cette op&eacute;ration sera enregistr&eacute;e lors de la prochaine sauvegarde.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={() => onRemove(action.id)}>
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  )
}

// -- ActionMobileCard sub-component --

function ActionMobileCard({ action, lien, isExpanded, onToggleExpand, onUpdate, onToggleComplete, onRemove }: {
  action: ActionPlan
  lien?: string
  isExpanded: boolean
  onToggleExpand: () => void
  onUpdate: (id: string, field: keyof ActionPlan, value: string | string[]) => void
  onToggleComplete: (id: string) => void
  onRemove: (id: string) => void
}) {
  const status = getActionStatus(action)
  const config = STATUS_CONFIG[status]
  const StatusIcon = config.icon
  const isComplete = !!action.termineLe

  return (
    <Card className={cn(
      'transition-all',
      isComplete && 'border-green-200 bg-green-50/30',
      status === 'echu' && 'border-red-200 bg-red-50/20',
    )}>
      <div
        className="p-4 cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <div className="pt-0.5" onClick={e => e.stopPropagation()}>
            <Checkbox
              checked={isComplete}
              onCheckedChange={() => onToggleComplete(action.id)}
              className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {capitalizeTheme(action.theme)}
              </Badge>
              <Badge variant={config.variant} className="gap-1 text-[10px] px-1.5 py-0">
                <StatusIcon className="size-2.5" />
                {config.label}
              </Badge>
            </div>
            <h3 className={cn(
              'text-sm font-medium text-foreground',
              isComplete && 'line-through text-muted-foreground'
            )}>
              {action.libelle}
            </h3>
            {/* Mini-info when collapsed */}
            {!isExpanded && (action.responsable || action.dateFin) && (
              <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                {action.responsable && <span>{action.responsable}</span>}
                {action.dateFin && (
                  <span className={cn(status === 'echu' && 'text-destructive font-medium')}>
                    Éch. {new Date(action.dateFin).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </div>
            )}
          </div>

          <ChevronDown className={cn(
            'size-4 text-muted-foreground transition-transform shrink-0 mt-1',
            isExpanded && 'rotate-180'
          )} />
        </div>
      </div>

      {isExpanded && (
        <CardContent className="pt-0 pb-4 space-y-3">
          {/* Metiers */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">M&eacute;tiers</label>
            <div className="flex flex-wrap gap-1.5">
              {action.metierIds.map(mid => (
                <Badge key={mid} variant="secondary" className="text-xs">
                  {action.metierNames[mid] || mid}
                </Badge>
              ))}
            </div>
          </div>

          {/* Responsable */}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">Responsable</label>
            <Input
              type="text"
              value={action.responsable}
              onChange={e => onUpdate(action.id, 'responsable', e.target.value)}
              placeholder="Nom du responsable..."
              disabled={isComplete}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">D&eacute;but</label>
              <Input
                type="date"
                value={action.dateDebut}
                onChange={e => onUpdate(action.id, 'dateDebut', e.target.value)}
                disabled={isComplete}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">&Eacute;ch&eacute;ance</label>
              <Input
                type="date"
                value={action.dateFin}
                onChange={e => onUpdate(action.id, 'dateFin', e.target.value)}
                className={cn(status === 'echu' && 'border-destructive text-destructive')}
                disabled={isComplete}
              />
            </div>
          </div>

          {/* Completion date */}
          {isComplete && action.termineLe && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle2 className="size-4 text-green-600" />
              <span className="text-sm text-green-700">
                Termin&eacute;e le {new Date(action.termineLe).toLocaleDateString('fr-FR')}
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant={isComplete ? 'outline' : 'default'}
              size="sm"
              className={cn(
                'flex-1',
                isComplete && 'border-green-200 text-green-700 hover:bg-green-50'
              )}
              onClick={() => onToggleComplete(action.id)}
            >
              {isComplete ? (
                <><CheckCircle2 className="size-4 mr-1.5" /> Rouvrir</>
              ) : (
                <><CheckCircle2 className="size-4 mr-1.5" /> Terminer</>
              )}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10">
                  <Trash2 className="size-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent size="sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer cette action ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    L'action sera retir&eacute;e du plan.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction variant="destructive" onClick={() => onRemove(action.id)}>
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {lien && (
            <a
              href={lien}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-muted rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="size-4" />
              Ressource OPPBTP
            </a>
          )}
        </CardContent>
      )}
    </Card>
  )
}
